import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {CSS} from '@dnd-kit/utilities';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  AlarmClock,
  ArrowRight,
  CalendarClock,
  CircleDollarSign,
  Clock3,
  FileQuestion,
  GripVertical,
  Plus,
  RefreshCw,
  Search,
  ShieldAlert,
  Sparkles,
} from 'lucide-react';
import {FormEvent, useMemo, useState} from 'react';
import {Link} from '../router';
import {ApiError, api} from '../api';
import {useWorkOperation} from '../hooks/useWorkOperation';
import {Badge, Modal, PrimaryButton, SecondaryButton, Surface, TextInput} from '../design';
import {EmptyPanel, ErrorPanel, LoadingPanel, PageHeader, formatDateTime, formatRelativeDate} from '../components/PagePrimitives';
import {ALL_OPPORTUNITY_STAGES, OpportunityBoardControls} from '../components/OpportunityBoardControls';
import {IntegrationStatus, OPPORTUNITY_STAGES, Opportunity, OpportunityStage, humanizeStage} from '../types';

const unhealthyStates = new Set(['stale', 'blocked', 'error']);
const integrationLabels: Record<IntegrationStatus['provider'], string> = {
  gmail: 'Gmail synchronization',
  calendar: 'Google Calendar',
  apify: 'Job discovery',
  openai: 'AI workspace',
  extension: 'Browser companion',
};
const sourceLabels = {linkedin: 'LinkedIn', indeed: 'Indeed'} as const;

const OpportunityCard = ({
  opportunity,
  move,
  moving,
}: {
  opportunity: Opportunity;
  move: (stage: OpportunityStage) => void;
  moving: boolean;
}) => {
  const draggable = useDraggable({
    id: opportunity.id,
    data: {opportunity},
    disabled: opportunity.allowedStageTransitions.length === 0,
  });

  return (
    <article
      ref={draggable.setNodeRef}
      className={'pd-opportunity-card' + (draggable.isDragging ? ' is-dragging' : '')}
      style={{transform: CSS.Translate.toString(draggable.transform)}}
    >
      <div className="pd-card-topline">
        <button
          ref={draggable.setActivatorNodeRef}
          type="button"
          className="pd-drag-handle"
          aria-label={'Move ' + opportunity.title + ' by keyboard or drag'}
          {...draggable.attributes}
          {...draggable.listeners}
        >
          <GripVertical size={16} aria-hidden="true" />
        </button>
        <span className="pd-source-label">{opportunity.source ?? 'manual'}</span>
        {typeof opportunity.fitScore === 'number' ? (
          <Badge className="pd-fit-badge">{Math.round(opportunity.fitScore)} fit</Badge>
        ) : null}
      </div>
      <h4>
        <Link className="pd-card-link" to={'/opportunities/' + opportunity.id}>{opportunity.title}</Link>
      </h4>
      <p className="pd-company">{opportunity.companyName}</p>
      <dl className="pd-card-facts">
        {opportunity.location ? (
          <div>
            <dt className="pd-visually-hidden">Location</dt>
            <dd>{opportunity.location}</dd>
          </div>
        ) : null}
        {opportunity.compensation ? (
          <div>
            <dt className="pd-visually-hidden">Compensation</dt>
            <dd>
              <CircleDollarSign size={14} aria-hidden="true" />
              {opportunity.compensation}
            </dd>
          </div>
        ) : null}
      </dl>
      {opportunity.blockers?.length ? (
        <p className="pd-blocker">
          <ShieldAlert size={15} aria-hidden="true" />
          {opportunity.blockers[0]}
        </p>
      ) : null}
      {opportunity.nextAction ? (
        <div className="pd-next-action">
          <span>Next</span>
          <p>{opportunity.nextAction}</p>
        </div>
      ) : null}
      <div className="pd-card-footer">
        <span>{formatRelativeDate(opportunity.lastActivityAt)}</span>
        <label>
          <span className="pd-visually-hidden">Change stage for {opportunity.title}</span>
          <select
            value={opportunity.stage}
            disabled={moving}
            onChange={(event) => move(event.currentTarget.value as OpportunityStage)}
          >
            {[opportunity.stage, ...opportunity.allowedStageTransitions].map((stage) => (
              <option key={stage} value={stage}>
                {humanizeStage(stage)}
              </option>
            ))}
          </select>
        </label>
      </div>
    </article>
  );
};

const StageColumn = ({
  stage,
  opportunities,
  move,
  movingId,
}: {
  stage: OpportunityStage;
  opportunities: Opportunity[];
  move: (id: string, stage: OpportunityStage) => void;
  movingId?: string;
}) => {
  const droppable = useDroppable({id: stage});
  return (
    <section
      ref={droppable.setNodeRef}
      className={'pd-board-column' + (droppable.isOver ? ' is-over' : '')}
      aria-labelledby={'stage-' + stage}
    >
      <header>
        <h3 id={'stage-' + stage}>{humanizeStage(stage)}</h3>
        <span>{opportunities.length}</span>
      </header>
      <div className="pd-column-list">
        {opportunities.map((opportunity) => (
          <OpportunityCard
            key={opportunity.id}
            opportunity={opportunity}
            move={(next) => move(opportunity.id, next)}
            moving={movingId === opportunity.id}
          />
        ))}
        {opportunities.length === 0 ? <p className="pd-column-empty">Drop or move an opportunity here.</p> : null}
      </div>
    </section>
  );
};

export const OpportunitiesPage = () => {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [visibleStages, setVisibleStages] = useState<OpportunityStage[]>([...OPPORTUNITY_STAGES]);
  const [createOpen, setCreateOpen] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const [title, setTitle] = useState('');
  const [sourceUrl, setSourceUrl] = useState('');
  const [location, setLocation] = useState('');
  const [compensation, setCompensation] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [stageMessage, setStageMessage] = useState('');
  const [discoveryOperationId, setDiscoveryOperationId] = useState<string | null>(null);
  const [gmailOperationId, setGmailOperationId] = useState<string | null>(null);

  const opportunities = useQuery({queryKey: ['opportunities'], queryFn: api.opportunities});
  const bootstrap = useQuery({queryKey: ['bootstrap'], queryFn: api.bootstrap});

  const retryDiscovery = useMutation({
    mutationFn: api.runDiscovery,
    onSuccess: (result) => setDiscoveryOperationId(result.operationId),
  });
  const retryGmail = useMutation({
    mutationFn: api.syncGmail,
    onSuccess: (result) => setGmailOperationId(result.operationId),
  });
  const discoveryInvalidations = useMemo(() => [['bootstrap'], ['integrations'], ['discovery-results']] as const, []);
  const gmailInvalidations = useMemo(() => [['bootstrap'], ['integrations'], ['opportunities'], ['relationships'], ['action-items']] as const, []);
  const discoveryOperation = useWorkOperation(discoveryOperationId, discoveryInvalidations);
  const gmailOperation = useWorkOperation(gmailOperationId, gmailInvalidations);
  const discoveryRetryActive = discoveryOperation.data?.state === 'queued' || discoveryOperation.data?.state === 'running';
  const gmailRetryActive = gmailOperation.data?.state === 'queued' || gmailOperation.data?.state === 'running';

  const staleSources = useMemo(() => {
    const discovery = bootstrap.data?.integrations.find((integration) => integration.provider === 'apify');
    return (['linkedin', 'indeed'] as const).flatMap((source) => {
      const health = discovery?.sourceStates?.[source];
      return health && unhealthyStates.has(health.state) ? [{source, health}] : [];
    });
  }, [bootstrap.data]);
  const staleIntegrations = useMemo(
    () => (bootstrap.data?.integrations ?? []).filter((integration) =>
      unhealthyStates.has(integration.state)
      && !(integration.provider === 'apify' && staleSources.length > 0)),
    [bootstrap.data, staleSources.length],
  );

  const move = useMutation({
    mutationFn: ({id, stage}: {id: string; stage: OpportunityStage}) => api.moveOpportunity(id, stage),
    onMutate: async ({id, stage}) => {
      setStageMessage('');
      await queryClient.cancelQueries({queryKey: ['opportunities']});
      const previous = queryClient.getQueryData<{items: Opportunity[]}>(['opportunities']);
      queryClient.setQueryData<{items: Opportunity[]}>(['opportunities'], (current) =>
        current
          ? {items: current.items.map((item) => (item.id === id ? {...item, stage} : item))}
          : current,
      );
      return {previous};
    },
    onError: (error, _variables, context) => {
      if (context?.previous) queryClient.setQueryData(['opportunities'], context.previous);
      setStageMessage(error instanceof ApiError && error.code === 'invalid_opportunity_transition'
        ? 'This opportunity changed elsewhere. The board was refreshed with its current valid moves.'
        : error instanceof Error ? error.message : 'The stage change could not be saved.');
    },
    onSettled: () => queryClient.invalidateQueries({queryKey: ['opportunities']}),
  });

  const create = useMutation({
    mutationFn: () =>
      api.createOpportunity({
        companyName,
        title,
        stage: 'interested',
        source: 'manual',
        ...(sourceUrl.trim() ? {sourceUrl: sourceUrl.trim()} : {}),
        ...(location.trim() ? {location: location.trim()} : {}),
        ...(compensation.trim() ? {compensation: compensation.trim()} : {}),
        ...(nextAction.trim() ? {nextAction: nextAction.trim()} : {}),
      }),
    onSuccess: async () => {
      setCompanyName('');
      setTitle('');
      setSourceUrl('');
      setLocation('');
      setCompensation('');
      setNextAction('');
      setCreateOpen(false);
      await queryClient.invalidateQueries({queryKey: ['opportunities']});
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {activationConstraint: {distance: 6}}),
    useSensor(KeyboardSensor),
  );

  const filtered = useMemo(() => {
    const items = opportunities.data?.items ?? [];
    const needle = query.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) =>
      [item.title, item.companyName, item.location, item.nextAction].some((value) =>
        value?.toLowerCase().includes(needle),
      ),
    );
  }, [opportunities.data, query]);

  const onDragEnd = (event: DragEndEvent) => {
    const opportunity = event.active.data.current?.opportunity as Opportunity | undefined;
    const stage = event.over?.id as OpportunityStage | undefined;
    if (opportunity && stage && ALL_OPPORTUNITY_STAGES.includes(stage) && opportunity.stage !== stage) {
      if (opportunity.allowedStageTransitions.includes(stage)) move.mutate({id: opportunity.id, stage});
      else setStageMessage(`${humanizeStage(stage)} is not a valid next stage from ${humanizeStage(opportunity.stage)}.`);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Your active search"
        title="Move the right opportunities forward."
        description="A selective pipeline for roles worth your time. Every card keeps its evidence, people, promises, and application history together."
        actions={
          <PrimaryButton leftSection={<Plus size={17} aria-hidden="true" />} onClick={() => setCreateOpen(true)}>
            Add opportunity
          </PrimaryButton>
        }
      />

      <section
        className="pd-action-strip pd-home-summary"
        aria-label="Required attention summary"
        tabIndex={0}
      >
        <Surface>
          <ShieldAlert size={20} aria-hidden="true" />
          <div>
            <strong>{bootstrap.data?.homeSummary?.approvals ?? '—'}</strong>
            <span>approvals waiting</span>
          </div>
          <Link to="/action-inbox">Review</Link>
        </Surface>
        <Surface>
          <FileQuestion size={20} aria-hidden="true" />
          <div>
            <strong>{bootstrap.data?.homeSummary?.missingInformation ?? '—'}</strong>
            <span>applications blocked</span>
          </div>
          <Link to="/action-inbox">Answer</Link>
        </Surface>
        <Surface>
          <CalendarClock size={20} aria-hidden="true" />
          <div>
            <strong>{bootstrap.data?.homeSummary?.interviews ?? '—'}</strong>
            <span>interviews to prepare</span>
          </div>
          <Link to="/action-inbox">Prepare</Link>
        </Surface>
        <Surface>
          <AlarmClock size={20} aria-hidden="true" />
          <div>
            <strong>{bootstrap.data?.homeSummary?.overdueCommitments ?? '—'}</strong>
            <span>overdue commitments</span>
          </div>
          <Link to="/action-inbox">Resolve</Link>
        </Surface>
        <Surface>
          <Clock3 size={20} aria-hidden="true" />
          <div>
            <strong>{bootstrap.data?.homeSummary?.staleOpportunities ?? '—'}</strong>
            <span>no movement for 14+ days</span>
          </div>
          <a href="#opportunity-board">Inspect</a>
        </Surface>
        <Surface>
          <Sparkles size={20} aria-hidden="true" />
          <div>
            <strong>{bootstrap.data?.homeSummary?.discoveryAwaitingDecision ?? '—'}</strong>
            <span>discovery decisions waiting</span>
          </div>
          <Link to="/discover">Decide</Link>
        </Surface>
      </section>

      {staleSources.map(({source, health}) => (
        <div className="pd-stale-banner pd-home-health" role="alert" key={source}>
          <div>
            <strong>{sourceLabels[source]} discovery data is stale.</strong>
            <span>
              Last successful synchronization: {health.lastSuccessfulAt ? formatDateTime(health.lastSuccessfulAt) : 'Never'}
            </span>
            <span>Last attempt: {health.lastAttemptAt ? formatDateTime(health.lastAttemptAt) : 'Unknown'}</span>
            {health.message ? <span>{health.message}</span> : null}
          </div>
          <div className="pd-home-health-actions">
            <SecondaryButton
              disabled={bootstrap.data?.capabilities?.discovery.usable === false}
              loading={retryDiscovery.isPending || discoveryRetryActive}
              leftSection={<RefreshCw size={15} aria-hidden="true" />}
              onClick={() => retryDiscovery.mutate()}
            >
              Run again
            </SecondaryButton>
            <Link to="/discover">Open discovery</Link>
          </div>
        </div>
      ))}
      {staleIntegrations.map((integration) => (
        <div className="pd-stale-banner pd-home-health" role="alert" key={integration.provider}>
          <div>
            <strong>{integrationLabels[integration.provider]} is {integration.state}.</strong>
            <span>
              Last successful synchronization: {integration.lastSuccessfulAt ? formatDateTime(integration.lastSuccessfulAt) : 'Never'}
            </span>
            <span>Last attempt: {integration.lastAttemptAt ? formatDateTime(integration.lastAttemptAt) : 'Unknown'}</span>
            {integration.message ? <span>{integration.message}</span> : null}
          </div>
          <div className="pd-home-health-actions">
            {integration.provider === 'gmail' ? (
              <SecondaryButton
                disabled={bootstrap.data?.capabilities?.gmail.usable === false}
                loading={retryGmail.isPending || gmailRetryActive}
                leftSection={<RefreshCw size={15} aria-hidden="true" />}
                onClick={() => retryGmail.mutate()}
              >
                Retry sync
              </SecondaryButton>
            ) : integration.provider === 'apify' ? (
              <SecondaryButton
                disabled={bootstrap.data?.capabilities?.discovery.usable === false}
                loading={retryDiscovery.isPending || discoveryRetryActive}
                leftSection={<RefreshCw size={15} aria-hidden="true" />}
                onClick={() => retryDiscovery.mutate()}
              >
                Run again
              </SecondaryButton>
            ) : null}
            <Link to="/settings">Open settings</Link>
          </div>
        </div>
      ))}
      {retryDiscovery.isError || retryGmail.isError || discoveryOperation.data?.state.startsWith('failed') || gmailOperation.data?.state.startsWith('failed') ? (
        <p className="pd-form-error" role="alert">
          {discoveryOperation.data?.message ?? gmailOperation.data?.message ?? ((retryDiscovery.error ?? retryGmail.error) instanceof Error
            ? (retryDiscovery.error ?? retryGmail.error)?.message
            : 'The integration retry could not be completed.')}
        </p>
      ) : null}

      <div className="pd-board-toolbar" id="opportunity-board">
        <label className="pd-search-control">
          <Search size={17} aria-hidden="true" />
          <span className="pd-visually-hidden">Filter opportunities</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
            placeholder="Filter by role, company, location, or next action"
          />
        </label>
        <OpportunityBoardControls
          query={query}
          visibleStages={visibleStages}
          onApply={({query: nextQuery, visibleStages: nextStages}) => {
            setQuery(nextQuery);
            setVisibleStages(nextStages);
          }}
        />
        <span>{filtered.filter((item) => visibleStages.includes(item.stage)).length} visible</span>
      </div>

      {opportunities.isLoading ? <LoadingPanel label="Loading opportunities" /> : null}
      {opportunities.isError ? (
        <ErrorPanel error={opportunities.error} retry={() => void opportunities.refetch()} />
      ) : null}
      {opportunities.data && opportunities.data.items.length === 0 ? (
        <EmptyPanel
          title="Your opportunity board is clear."
          description="Add a role manually or turn a discovery result into an opportunity when it deserves serious attention."
          action={
            <PrimaryButton onClick={() => setCreateOpen(true)} leftSection={<Plus size={16} aria-hidden="true" />}>
              Add your first role
            </PrimaryButton>
          }
        />
      ) : null}
      {opportunities.data && opportunities.data.items.length > 0 ? (
        <DndContext
          sensors={sensors}
          onDragEnd={onDragEnd}
          accessibility={{
            announcements: {
              onDragStart: ({active}) => 'Picked up opportunity ' + String(active.id) + '.',
              onDragOver: ({over}) => (over ? 'Over ' + humanizeStage(String(over.id)) + '.' : ''),
              onDragEnd: ({active, over}) => {
                const item = active.data.current?.opportunity as Opportunity | undefined;
                if (!over || !item) return 'Move cancelled.';
                return item.allowedStageTransitions.includes(String(over.id) as OpportunityStage)
                  ? 'Moved ' + String(active.id) + ' to ' + humanizeStage(String(over.id)) + '.'
                  : humanizeStage(String(over.id)) + ' is not a valid next stage.';
              },
              onDragCancel: ({active}) => 'Move cancelled for ' + String(active.id) + '.',
            },
          }}
        >
          <div className="pd-board" aria-label="Opportunity stages">
            {visibleStages.map((stage) => (
              <StageColumn
                key={stage}
                stage={stage}
                opportunities={filtered.filter((item) => item.stage === stage)}
                move={(id, next) => move.mutate({id, stage: next})}
                movingId={move.isPending ? move.variables?.id : undefined}
              />
            ))}
          </div>
        </DndContext>
      ) : null}
      {stageMessage ? (
        <p className="pd-toast pd-toast--error" role="alert">
          {stageMessage}
        </p>
      ) : null}

      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title="Add an opportunity" centered>
        <form
          className="pd-modal-form"
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <TextInput
            label="Company"
            required
            value={companyName}
            onChange={(event) => setCompanyName(event.currentTarget.value)}
            placeholder="Example: Acme"
          />
          <TextInput
            label="Role"
            required
            value={title}
            onChange={(event) => setTitle(event.currentTarget.value)}
            placeholder="Example: Staff Software Engineer"
          />
          <TextInput
            label="Posting URL"
            description="Paste the job posting link so the source stays attached. Optional."
            type="url"
            value={sourceUrl}
            onChange={(event) => setSourceUrl(event.currentTarget.value)}
            placeholder="https://boards.example.com/roles/1234"
            maxLength={2_048}
          />
          <TextInput
            label="Location"
            value={location}
            onChange={(event) => setLocation(event.currentTarget.value)}
            placeholder="Example: Remote (US)"
            maxLength={240}
          />
          <TextInput
            label="Compensation"
            value={compensation}
            onChange={(event) => setCompensation(event.currentTarget.value)}
            placeholder="Example: $210k–$240k + equity"
            maxLength={500}
          />
          <TextInput
            label="Next move"
            description="What has to happen next so this role does not go quiet."
            value={nextAction}
            onChange={(event) => setNextAction(event.currentTarget.value)}
            placeholder="Example: Email Dana the referral request by Friday"
            maxLength={1_000}
          />
          {create.isError ? (
            <p className="pd-form-error" role="alert">
              {create.error instanceof Error ? create.error.message : 'The opportunity could not be added.'}
            </p>
          ) : null}
          <div className="pd-form-actions">
            <SecondaryButton type="button" onClick={() => setCreateOpen(false)}>
              Cancel
            </SecondaryButton>
            <PrimaryButton type="submit" loading={create.isPending} rightSection={<ArrowRight size={16} />}>
              Add to Interested
            </PrimaryButton>
          </div>
        </form>
      </Modal>
    </>
  );
};
